import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, TrendingUp, Target, Award, LineChart, Users, Zap, Mail, Calendar, ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import expertProfile from "@/assets/expert-profile.jpg";
import { ReportFooter } from "@/components/ReportFooter";
declare global {
  interface Window {
    Cal: any;
  }
}
const ThankYou = () => {
  const heroStats = [{
    value: "277%",
    label: "Więcej leadów niż Facebook i Twitter razem",
    icon: <TrendingUp className="h-8 w-8 md:h-10 md:w-10" />
  }, {
    value: "80%",
    label: "Leadów B2B pochodzi z LinkedIn",
    icon: <Target className="h-8 w-8 md:h-10 md:w-10" />
  }, {
    value: "2x",
    label: "Wyższy ROI z marketingu LinkedIn",
    icon: <LineChart className="h-8 w-8 md:h-10 md:w-10" />
  }, {
    value: "51%",
    label: "Większa szansa na osiągnięcie celów sprzedażowych",
    icon: <Award className="h-8 w-8 md:h-10 md:w-10" />
  }];
  const detailedResults = [{
    title: "Social Selling & Prospecting",
    mainStat: "78% sprzedawców używających social selling osiąga lepsze wyniki",
    bullets: ["51% większe szanse na quotę", "45% więcej okazji sprzedażowych", "Średnio 5.3x więcej potencjalnych klientów miesięcznie"],
    gradient: "from-primary/20 via-primary/10 to-background",
    icon: <Users className="h-12 w-12 text-primary" />
  }, {
    title: "Personal Branding",
    mainStat: "Budowanie autorytetu branżowego to klucz do zaufania i większej liczby leadów",
    bullets: ["Eksperci z silnym personal brandingiem generują 5x więcej leadów dla firmy", "Firmy z autorytetami branżowymi na LinkedIn budują zaufanie 3x szybciej", "Posty ekspertów mają 8x wyższy engagement niż oficjalne strony firmowe"],
    gradient: "from-amber-500/20 via-amber-500/10 to-background",
    icon: <Award className="h-12 w-12 text-amber-600" />
  }, {
    title: "Lead Generation",
    mainStat: "46% całego social media traffic B2B z LinkedIn",
    bullets: ["2x więcej leadów przy regularnym publikowaniu", "92% konsumentów ufa ludziom, nie markom", "60% kupuje od marek dzięki personal reputation"],
    gradient: "from-emerald-500/20 via-emerald-500/10 to-background",
    icon: <TrendingUp className="h-12 w-12 text-emerald-600" />
  }];
  const nextSteps = [{
    icon: <Mail className="h-6 w-6 text-primary" />,
    title: "Sprawdź swoją skrzynkę email",
    description: "Niedługo dostaniesz od nas wiadomość z możliwością uzyskania szczegółowego audytu Linkedin Social Selling dla Twojej konkurencji z zaleceniami jak ich wyprzedzić."
  }, {
    icon: <Calendar className="h-6 w-6 text-primary" />,
    title: "Zarezerwuj bezpłatną konsultację",
    description: "Nie czekaj i umów się na darmową konsultacje z ekspertem"
  }, {
    icon: <Zap className="h-6 w-6 text-primary" />,
    title: "Rozpocznij wdrażanie sprawdzonych silników sprzedażowych",
    description: "Zleć prowadzenie LinkedIn Social Selling nam i oczekuj szybkich rezultatów"
  }];

  // Load Cal.com embed script
  useEffect(() => {
    (function (C, A, L) {
      let p = function (a: any, ar: any) {
        a.q.push(ar);
      };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () {
            p(api, arguments);
          };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");
    window.Cal("init", "social-selling", {
      origin: "https://app.cal.com"
    });
  }, []);

  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'ThankYouPageViewed', {
        timestamp: new Date().toISOString()
      });
    }
  }, []);
  return <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-8 md:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto space-y-8 md:space-y-12 pb-16">
            
            {/* Hero Section */}
            <div className="text-center animate-fade-in">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mb-6 animate-scale-in">
                <CheckCircle2 className="h-8 w-8 md:h-10 md:w-10 text-primary" />
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
                Dziękujemy!
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-4">
                Jesteś o krok od wykorzystania potencjału LinkedIn
              </p>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                Tymczasem zobacz, jakie rezultaty osiągają firmy dzięki LinkedIn
              </p>
            </div>

            {/* Hero Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {heroStats.map((stat, index) => <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-primary/20 via-primary/10 to-background hover:shadow-xl hover:scale-105 transition-all animate-fade-in" style={{
              animationDelay: `${index * 100}ms`
            }}>
                  <CardContent className="p-4 md:p-6 text-center">
                    <div className="flex justify-center mb-3 md:mb-4 text-primary">
                      {stat.icon}
                    </div>
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-2 md:mb-3">
                      {stat.value}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                      {stat.label}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            {/* Detailed Results Section */}
            <div className="space-y-4 md:space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                  Sprawdzone rezultaty LinkedIn
                </h2>
                <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                  Dane z tysięcy kampanii B2B pokazują realne korzyści
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {detailedResults.map((result, index) => <Card key={index} className={`border-none shadow-xl overflow-hidden animate-fade-in hover:shadow-2xl transition-all`} style={{
                animationDelay: `${(index + 4) * 100}ms`
              }}>
                    <div className={`h-3 bg-gradient-to-r ${result.gradient}`} />
                    <CardHeader className="pb-4 bg-gradient-to-br from-secondary/30 to-background">
                      <div className="flex items-start gap-4 mb-3">
                        <div className="flex-shrink-0 bg-background/80 p-2 rounded-lg">
                          {result.icon}
                        </div>
                        <CardTitle className="text-lg md:text-xl leading-tight">
                          {result.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-base md:text-lg font-semibold text-foreground">
                        {result.mainStat}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 bg-gradient-to-br from-secondary/10 to-background">
                      {result.bullets.map((bullet, i) => <div key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm md:text-base text-muted-foreground">
                            {bullet}
                          </span>
                        </div>)}
                    </CardContent>
                  </Card>)}
              </div>
            </div>

            {/* Additional Stat - ROI */}
            <Card className="border-none shadow-xl bg-gradient-to-br from-primary/30 via-primary/15 to-secondary/20 hover:shadow-2xl transition-all">
              <CardContent className="p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
                  <div className="text-center p-4 rounded-xl bg-background/50 backdrop-blur-sm">
                    <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">2.74%</div>
                    <p className="text-sm md:text-base text-muted-foreground">Średnia conversion rate (najwyższa w social media)</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-background/50 backdrop-blur-sm">
                    <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">6x</div>
                    <p className="text-sm md:text-base text-muted-foreground">Wyższy engagement rate od innych platform</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-background/50 backdrop-blur-sm">
                    <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">+33%</div>
                    <p className="text-sm md:text-base text-muted-foreground">Wyższy średni deal size</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Steps Section */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl md:text-3xl mb-3">Nie chcesz czekać? Umów się na spotkanie.</CardTitle>
                <CardDescription className="text-base md:text-lg">
                  Twoje kolejne kroki do sukcesu na LinkedIn
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {nextSteps.map((step, index) => {
                // Special styling for consultation card with photo
                if (index === 1) {
                  return <div key={index} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/30 hover:shadow-xl transition-all border-2 border-primary/30">
                        <div className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8">
                          {/* Photo section */}
                          <div className="flex-shrink-0">
                            <div className="relative">
                              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
                              <img src={expertProfile} alt="Ekspertka LinkedIn Social Selling" className="relative w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-primary/30 shadow-2xl" />
                            </div>
                          </div>
                          
                          {/* Content section */}
                          <div className="flex-1 text-center md:text-left">
                            <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                              <div className="bg-primary/10 p-3 rounded-lg">
                                {step.icon}
                              </div>
                              <h3 className="font-bold text-xl md:text-2xl">{step.title}</h3>
                            </div>
                            <p className="text-base md:text-lg text-muted-foreground mb-4">
                              {step.description}
                            </p>
                            <button data-cal-namespace="social-selling" data-cal-link="lukasgmys/social-selling" data-cal-config='{"layout":"month_view"}' className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold shadow-lg hover:shadow-xl">
                              <Calendar className="h-5 w-5" />
                              Umów się na spotkanie
                            </button>
                          </div>
                        </div>
                      </div>;
                }
                return <div key={index} className="flex items-start gap-4 p-4 md:p-6 rounded-xl bg-gradient-to-br from-secondary/50 to-secondary/30 hover:shadow-md transition-all">
                      <div className="flex-shrink-0 bg-primary/10 p-3 rounded-lg">
                        {step.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-base md:text-lg mb-1">{step.title}</h3>
                        <p className="text-sm md:text-base text-muted-foreground">{step.description}</p>
                      </div>
                    </div>;
              })}
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="gradient-hero border-none shadow-2xl text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent" />
              <CardContent className="p-8 md:p-12 text-center relative z-10">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                  Gotowy na kolejny krok?
                </h2>
                <p className="text-base md:text-lg mb-8 opacity-90 max-w-2xl mx-auto">
                  Wróć do swojego raportu i zacznij wdrażać rekomendacje, lub umów się na konsultację z naszymi ekspertami
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 shadow-xl px-8 py-6 h-auto">
                    <a href="https://lifactor.com" target="_blank" rel="noopener noreferrer">
                      Sprawdź usługi LiFactor
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Sources */}
            <div className="text-center text-sm text-muted-foreground pt-4 pb-8">
              <p>
                Źródła: LinkedIn Sales Solutions, Sopro.io, Sprout Social, Breakcold, 2024-2025
              </p>
            </div>

          </div>
        </div>
      </div>
      <ReportFooter />
    </ScrollArea>;
};
export default ThankYou;